import { Input, InputProps } from "@/lib/components/Input";

type Props = Omit<InputProps, "value" | "onChange" | "className"> & {
  value: number;
  label: string;
  className?: string;
  allowNegativeValues?: boolean;
  onChange: (value: number) => void;
};

export default function ElementInput({
  value,
  label,
  className,
  allowNegativeValues = true,
  onChange,
  ...props
}: Props) {
  const commit = (rawValue: string) => {
    const result = evaluateMathExpression(rawValue);
    if (result === null) {
      return;
    }

    const rounded = Math.round(result);
    onChange(allowNegativeValues ? rounded : Math.max(0, rounded));
  };

  const step = (rawValue: string, delta: number) => {
    const base = evaluateMathExpression(rawValue) ?? value;
    const next = Math.round(base) + delta;
    onChange(allowNegativeValues ? next : Math.max(0, next));
  };

  return (
    <Input
      {...props}
      value={value}
      className={className}
      containerSize="sm"
      leadingComponent={<div className="text-text-secondary">{label}</div>}
      onValidateInternalValue={(e) => {
        // Allow digits and math operators so expressions like "24+16" can be typed.
        return /^[\d+\-*/().\s]*$/.test(e.target.value);
      }}
      onBlur={(e) => commit(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          commit(e.currentTarget.value);
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          step(e.currentTarget.value, 1);
        } else if (e.key === "ArrowDown") {
          e.preventDefault();
          step(e.currentTarget.value, -1);
        }
      }}
      onChange={(e) => {
        const value = e.target.value;
        // Live-update while typing a plain number; expressions commit on blur/Enter.
        const isPlainNumber = allowNegativeValues
          ? /^-?\d+$/.test(value)
          : /^\d+$/.test(value);

        if (isPlainNumber) {
          onChange(Number(value));
        }
      }}
    />
  );
}

// Safely evaluates a basic arithmetic expression (+, -, *, /, parentheses)
// without using eval. Returns null for invalid or incomplete input.
export function evaluateMathExpression(input: string): number | null {
  const tokens = input.match(/\d*\.?\d+|[+\-*/()]/g);

  if (!tokens) {
    return null;
  }

  // Reject input containing characters outside the valid token set (e.g. "40px").
  if (tokens.join("") !== input.replace(/\s/g, "")) {
    return null;
  }

  let pos = 0;
  const peek = () => tokens[pos];
  const consume = () => tokens[pos++];

  const parseExpression = (): number | null => {
    let left = parseTerm();
    if (left === null) return null;

    while (peek() === "+" || peek() === "-") {
      const op = consume();
      const right = parseTerm();
      if (right === null) return null;
      left = op === "+" ? left + right : left - right;
    }

    return left;
  };

  const parseTerm = (): number | null => {
    let left = parseFactor();
    if (left === null) return null;

    while (peek() === "*" || peek() === "/") {
      const op = consume();
      const right = parseFactor();
      if (right === null) return null;
      left = op === "*" ? left * right : left / right;
    }

    return left;
  };

  const parseFactor = (): number | null => {
    const token = peek();

    if (token === "(") {
      consume();
      const value = parseExpression();
      if (value === null || peek() !== ")") return null;
      consume();
      return value;
    }

    if (token === "-" || token === "+") {
      consume();
      const value = parseFactor();
      if (value === null) return null;
      return token === "-" ? -value : value;
    }

    if (token !== undefined && /^\d*\.?\d+$/.test(token)) {
      consume();
      return Number(token);
    }

    return null;
  };

  const result = parseExpression();

  if (result === null || pos !== tokens.length || !Number.isFinite(result)) {
    return null;
  }

  return result;
}
